// Viewport-State (Pan/Zoom) — bewusst lokaler React-State pro Canvas.
// Multi-Component-Coordination (z.B. Tool Rail steuert Zoom) bekommt spaeter zustand.
//
// World↔Screen:
//   screen = world * zoom + translate
//   world  = (screen - translate) / zoom

import { useCallback, useRef, useState } from "react";

export interface Viewport {
  zoom: number;
  tx: number;
  ty: number;
}

export const VIEWPORT_DEFAULT: Viewport = { zoom: 1, tx: 0, ty: 0 };

export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 2.5;

export interface PanToOptions {
  /** Animationsdauer in ms (Default 250). Bei prefers-reduced-motion immer 0. */
  duration?: number;
}

export interface UseViewportApi {
  viewport: Viewport;
  setZoom: (next: number) => void;
  zoomAt: (factor: number, screenX: number, screenY: number) => void;
  panBy: (dx: number, dy: number) => void;
  /** Pannt so, dass `worldX/worldY` im Mittelpunkt des Viewports landet. */
  panTo: (
    worldX: number,
    worldY: number,
    viewportWidth: number,
    viewportHeight: number,
    options?: PanToOptions,
  ) => void;
  reset: () => void;
  toWorld: (screenX: number, screenY: number) => { x: number; y: number };
  toScreen: (worldX: number, worldY: number) => { x: number; y: number };
}

export function useViewport(initial: Viewport = VIEWPORT_DEFAULT): UseViewportApi {
  const [viewport, setViewport] = useState<Viewport>(initial);
  const panAnimRef = useRef<number | null>(null);

  const setZoom = useCallback((next: number) => {
    setViewport((v) => ({ ...v, zoom: clamp(next, ZOOM_MIN, ZOOM_MAX) }));
  }, []);

  const zoomAt = useCallback((factor: number, screenX: number, screenY: number) => {
    setViewport((v) => {
      const nextZoom = clamp(v.zoom * factor, ZOOM_MIN, ZOOM_MAX);
      const realFactor = nextZoom / v.zoom;
      // Punkt unter dem Cursor soll konstant bleiben.
      const tx = screenX - (screenX - v.tx) * realFactor;
      const ty = screenY - (screenY - v.ty) * realFactor;
      return { zoom: nextZoom, tx, ty };
    });
  }, []);

  const panBy = useCallback((dx: number, dy: number) => {
    setViewport((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
  }, []);

  const reset = useCallback(() => setViewport(VIEWPORT_DEFAULT), []);

  const panTo = useCallback<UseViewportApi["panTo"]>(
    (worldX, worldY, viewportWidth, viewportHeight, options) => {
      if (panAnimRef.current !== null) {
        cancelAnimationFrame(panAnimRef.current);
        panAnimRef.current = null;
      }
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      const duration = reduceMotion ? 0 : (options?.duration ?? 250);
      let startTx = 0;
      let startTy = 0;
      let targetTx = 0;
      let targetTy = 0;
      setViewport((v) => {
        startTx = v.tx;
        startTy = v.ty;
        targetTx = viewportWidth / 2 - worldX * v.zoom;
        targetTy = viewportHeight / 2 - worldY * v.zoom;
        return duration === 0 ? { ...v, tx: targetTx, ty: targetTy } : v;
      });
      if (duration === 0) return;
      const t0 = performance.now();
      const tick = (now: number): void => {
        const t = Math.min(1, (now - t0) / duration);
        const ease = 1 - (1 - t) ** 3; // ease-out cubic
        setViewport((v) => ({
          ...v,
          tx: startTx + (targetTx - startTx) * ease,
          ty: startTy + (targetTy - startTy) * ease,
        }));
        if (t < 1) {
          panAnimRef.current = requestAnimationFrame(tick);
        } else {
          panAnimRef.current = null;
        }
      };
      panAnimRef.current = requestAnimationFrame(tick);
    },
    [],
  );

  const toWorld = useCallback(
    (screenX: number, screenY: number) => ({
      x: (screenX - viewport.tx) / viewport.zoom,
      y: (screenY - viewport.ty) / viewport.zoom,
    }),
    [viewport.tx, viewport.ty, viewport.zoom],
  );

  const toScreen = useCallback(
    (worldX: number, worldY: number) => ({
      x: worldX * viewport.zoom + viewport.tx,
      y: worldY * viewport.zoom + viewport.ty,
    }),
    [viewport.tx, viewport.ty, viewport.zoom],
  );

  return { viewport, setZoom, zoomAt, panBy, panTo, reset, toWorld, toScreen };
}

function clamp(value: number, lo: number, hi: number): number {
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}
